import { clearElement } from '../adapters/dom';
import { injectWidgetStyles } from './styles/inject-styles';
import { createErrorTemplate } from './templates/error';
import { createExpiredTemplate } from './templates/expired';
import { createLoadingTemplate } from './templates/loading';
import { createMethodSelectTemplate } from './templates/method-select';
import { createPaymentDetailsTemplate } from './templates/payment-details';
import { createSuccessTemplate } from './templates/success';
import type { WidgetStoreState } from '../types/state';

export interface RenderCallbacks {
  onMethodSelect: (methodCode: string) => void;
  onCancelPayment: () => void;
}

export interface RenderResult {
  setRemainingMs?: (remainingMs: number) => void;
}

function prepareRenderRoot(root: ShadowRoot | HTMLElement): HTMLDivElement {
  clearElement(root as HTMLElement);
  injectWidgetStyles(root);

  const appRoot = document.createElement('div');
  appRoot.className = 'cpw-root';

  root.append(appRoot);

  return appRoot;
}

export async function renderWidgetScreen(
  root: ShadowRoot | HTMLElement,
  state: WidgetStoreState,
  callbacks: RenderCallbacks
): Promise<RenderResult> {
  const appRoot = prepareRenderRoot(root);
  if (state.uiState === 'loading' || state.uiState === 'init') {
    appRoot.append(
      createLoadingTemplate({
        title: 'Загрузка',
        description: 'Подготавливаем платёжную форму...'
      })
    );
    return {};
  }

  if (state.uiState === 'method_select' && state.session) {
    appRoot.append(
      createMethodSelectTemplate({
        session: state.session,
        onSelect: callbacks.onMethodSelect
      })
    );
    return {};
  }

  if (
    ['quote_ready', 'address_assigned', 'awaiting_payment', 'payment_detected', 'payment_confirming'].includes(
      state.uiState
    ) &&
    state.selection
  ) {
    const paymentDetails = await createPaymentDetailsTemplate({
      selection: state.selection,
      status: state.status,
      onCancel: callbacks.onCancelPayment
    });

    appRoot.append(paymentDetails.element);

    return {
      setRemainingMs: paymentDetails.setRemainingMs
    };
  }

  if (state.uiState === 'success' && state.status) {
    appRoot.append(createSuccessTemplate(state.status));
    return {};
  }

  if (state.uiState === 'expired') {
    appRoot.append(createExpiredTemplate());
    return {};
  }

  if (state.uiState === 'cancelled') {
    appRoot.append(
      createErrorTemplate({
        title: 'Оплата отменена',
        message: 'Платёж был отменён пользователем.'
      })
    );
    return {};
  }

  if (state.uiState === 'partial_payment') {
    appRoot.append(
      createErrorTemplate({
        title: 'Недостаточная сумма',
        message: 'Получено меньше средств, чем требуется для оплаты.'
      })
    );
    return {};
  }

  if (state.uiState === 'overpayment') {
    appRoot.append(
      createErrorTemplate({
        title: 'Обнаружена переплата',
        message: 'Получено больше средств, чем ожидалось. Требуется обработка.'
      })
    );
    return {};
  }

  if (state.uiState === 'network_error') {
    appRoot.append(
      createErrorTemplate({
        title: 'Ошибка сети',
        message: state.lastError ?? 'Не удалось получить данные от сервера.'
      })
    );
    return {};
  }

  appRoot.append(
    createErrorTemplate({
      title: 'Ошибка',
      message: state.lastError ?? 'Что-то пошло не так.'
    })
  );

  return {};
}